import React, { useState, useMemo } from 'react';
import { 
  X, 
  Eye, 
  Smartphone, 
  Monitor, 
  Code, 
  Copy, 
  Check, 
  Send, 
  Sparkles, 
  Loader2, 
  ShieldCheck, 
  Mail, 
  RefreshCw,
  Zap
} from 'lucide-react';
import { 
  generateBrandedVerificationEmailHtml, 
  generateBrandedWelcomeEmailHtml 
} from '../../utils/emailTemplates';

interface EmailTemplatePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTriggerToast?: (title: string, message: string) => void;
}

export const EmailTemplatePreviewModal: React.FC<EmailTemplatePreviewModalProps> = ({
  isOpen,
  onClose,
  onTriggerToast,
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<'verification_otp' | 'welcome_email'>('verification_otp');
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile' | 'code'>('desktop');

  // Dynamic template variables
  const [simName, setSimName] = useState('Jordan Vance');
  const [simEmail, setSimEmail] = useState('elmejor15@gmail.com');
  const [simCode, setSimCode] = useState('849201');
  const [simSport, setSimSport] = useState('Basketball');
  const [copiedHtml, setCopiedHtml] = useState(false);

  // Test send state inside modal
  const [isSendingTest, setIsSendingTest] = useState(false);

  // Generate HTML based on state
  const generatedHtml = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://playgroundleague.pro';
    const verifyUrl = `${origin}/api/verify-email?email=${encodeURIComponent(simEmail)}&code=${simCode}`;

    if (selectedTemplate === 'verification_otp') {
      return generateBrandedVerificationEmailHtml({
        recipientName: simName || 'Athlete',
        recipientEmail: simEmail || 'athlete@example.com',
        verificationCode: simCode || '123456',
        directVerifyUrl: verifyUrl,
        appOrigin: origin,
      });
    } else {
      return generateBrandedWelcomeEmailHtml({
        recipientName: simName || 'Athlete',
        recipientEmail: simEmail || 'athlete@example.com',
        sport: simSport,
        isPro: true,
        directVerifyUrl: verifyUrl,
        appOrigin: origin,
      });
    }
  }, [selectedTemplate, simName, simEmail, simCode, simSport]);

  const handleCopyHtml = () => {
    navigator.clipboard?.writeText(generatedHtml);
    setCopiedHtml(true);
    onTriggerToast?.('Template Copied', 'Raw HTML copied to clipboard.');
    setTimeout(() => setCopiedHtml(false), 2200);
  };

  const handleSendTestFromModal = async () => {
    if (!simEmail) return;
    setIsSendingTest(true);
    try {
      const res = await fetch('/api/email/test-send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: simEmail,
          name: simName,
          type: selectedTemplate,
        }),
      });
      const data = await res.json();
      if (data.success) {
        onTriggerToast?.('Preview Dispatched', `Delivered template to ${simEmail} via ${data.provider.toUpperCase()}`);
      } else {
        onTriggerToast?.('Dispatch Notice', data.error || 'Check server configuration.');
      }
    } catch (err: any) {
      onTriggerToast?.('Dispatch Error', err?.message || 'Failed to send test email.');
    } finally {
      setIsSendingTest(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 animate-fadeIn">
      <div className="w-full max-w-5xl bg-indigo-950 border border-white/20 rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[92vh] text-white">
        
        {/* MODAL HEADER */}
        <div className="px-6 py-4 bg-indigo-900/80 border-b border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-lime-400 text-black rounded-2xl font-black">
              <Eye className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h2 className="text-lg font-black italic uppercase text-white tracking-wide">
                  Branded HTML Email Template Inspector
                </h2>
                <span className="px-2 py-0.5 rounded-full bg-lime-400/20 text-lime-300 border border-lime-400/30 text-[9px] font-black uppercase font-mono">
                  TAILWIND CSS COMPLIANT
                </span>
              </div>
              <p className="text-xs text-indigo-300">
                Inspect responsive HTML layout, dark court palette, and 6-digit OTP security block before dispatch
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Viewport Switcher */}
            <div className="flex items-center bg-indigo-950 p-1 rounded-xl border border-white/10">
              <button
                type="button"
                onClick={() => setPreviewDevice('desktop')}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition flex items-center space-x-1 ${
                  previewDevice === 'desktop' ? 'bg-lime-400 text-black' : 'text-indigo-300 hover:text-white'
                }`}
                title="Desktop View (600px)"
              >
                <Monitor className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Desktop</span>
              </button>

              <button
                type="button"
                onClick={() => setPreviewDevice('mobile')}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition flex items-center space-x-1 ${
                  previewDevice === 'mobile' ? 'bg-lime-400 text-black' : 'text-indigo-300 hover:text-white'
                }`}
                title="Mobile View (375px)"
              >
                <Smartphone className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Mobile</span>
              </button>

              <button
                type="button"
                onClick={() => setPreviewDevice('code')}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition flex items-center space-x-1 ${
                  previewDevice === 'code' ? 'bg-lime-400 text-black' : 'text-indigo-300 hover:text-white'
                }`}
                title="Raw HTML Source"
              >
                <Code className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Source</span>
              </button>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="p-2 text-indigo-300 hover:text-white hover:bg-white/10 rounded-xl transition"
              title="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* CONTROLS BAR: TEMPLATE SELECTOR & SAMPLE PARAMS */}
        <div className="px-6 py-3 bg-indigo-900/40 border-b border-white/10 grid grid-cols-1 sm:grid-cols-12 gap-3 items-center text-xs">
          
          {/* Template Choice */}
          <div className="sm:col-span-4 flex items-center space-x-2">
            <span className="text-[10px] font-black uppercase text-indigo-400 shrink-0">Template:</span>
            <select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value as any)}
              className="w-full bg-indigo-950 border border-white/10 rounded-xl px-3 py-1.5 text-white font-bold text-xs outline-none focus:border-lime-400"
            >
              <option value="verification_otp">⚡ Athlete 6-Digit OTP Verification</option>
              <option value="welcome_email">🏀 Official League Welcome & Onboarding</option>
            </select>
          </div>

          {/* Recipient Name */}
          <div className="sm:col-span-3 flex items-center space-x-2">
            <span className="text-[10px] font-black uppercase text-indigo-400 shrink-0">Name:</span>
            <input
              type="text"
              value={simName}
              onChange={(e) => setSimName(e.target.value)}
              className="w-full bg-indigo-950 border border-white/10 rounded-xl px-2.5 py-1 text-white text-xs outline-none focus:border-lime-400"
              placeholder="e.g. Jordan Vance"
            />
          </div>

          {/* OTP Code (if OTP template) or Sport */}
          {selectedTemplate === 'verification_otp' ? (
            <div className="sm:col-span-2 flex items-center space-x-2">
              <span className="text-[10px] font-black uppercase text-indigo-400 shrink-0">OTP:</span>
              <input
                type="text"
                maxLength={6}
                value={simCode}
                onChange={(e) => setSimCode(e.target.value)}
                className="w-full bg-indigo-950 border border-white/10 rounded-xl px-2 py-1 text-lime-300 font-mono font-black text-xs text-center outline-none focus:border-lime-400"
                placeholder="6 digits"
              />
            </div>
          ) : (
            <div className="sm:col-span-2 flex items-center space-x-2">
              <span className="text-[10px] font-black uppercase text-indigo-400 shrink-0">Sport:</span>
              <input
                type="text"
                value={simSport}
                onChange={(e) => setSimSport(e.target.value)}
                className="w-full bg-indigo-950 border border-white/10 rounded-xl px-2 py-1 text-amber-300 font-bold text-xs outline-none focus:border-lime-400"
              />
            </div>
          )}

          {/* Test Email Target */}
          <div className="sm:col-span-3 flex items-center space-x-2">
            <input
              type="email"
              value={simEmail}
              onChange={(e) => setSimEmail(e.target.value)}
              className="w-full bg-indigo-950 border border-white/10 rounded-xl px-2.5 py-1 text-white text-xs outline-none focus:border-lime-400 font-mono"
              placeholder="recipient@domain.com"
            />
          </div>
        </div>

        {/* PREVIEW VIEWPORT AREA */}
        <div className="flex-1 overflow-y-auto bg-black/90 p-4 sm:p-6 flex items-center justify-center min-h-[420px]">
          {previewDevice === 'code' ? (
            <div className="w-full h-full max-h-[520px] bg-slate-950 border border-white/10 rounded-2xl p-4 overflow-y-auto font-mono text-xs text-lime-300/90 relative">
              <button
                onClick={handleCopyHtml}
                className="absolute top-4 right-4 px-3 py-1.5 bg-indigo-900 hover:bg-indigo-800 text-white rounded-xl text-xs font-bold transition flex items-center space-x-1.5 border border-white/10"
              >
                {copiedHtml ? <Check className="w-3.5 h-3.5 text-lime-400" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedHtml ? 'Copied!' : 'Copy Raw HTML'}</span>
              </button>
              <pre className="whitespace-pre-wrap break-all pr-24">{generatedHtml}</pre>
            </div>
          ) : previewDevice === 'mobile' ? (
            /* Mobile Frame */
            <div className="w-[375px] max-w-full bg-slate-900 border-4 border-slate-700 rounded-[2.5rem] p-3 shadow-2xl flex flex-col items-center">
              {/* Notch */}
              <div className="w-28 h-4 bg-slate-800 rounded-full mb-3 flex items-center justify-center">
                <span className="w-2 h-2 rounded-full bg-slate-900"></span>
              </div>
              <div className="w-full bg-black rounded-2xl overflow-hidden max-h-[500px] overflow-y-auto border border-white/5">
                <div dangerouslySetInnerHTML={{ __html: generatedHtml }} />
              </div>
            </div>
          ) : (
            /* Desktop Container Frame (Max 620px) */
            <div className="w-full max-w-[620px] bg-black rounded-3xl overflow-hidden shadow-2xl border border-white/10 max-h-[520px] overflow-y-auto">
              <div dangerouslySetInnerHTML={{ __html: generatedHtml }} />
            </div>
          )}
        </div>

        {/* MODAL FOOTER ACTIONS */}
        <div className="px-6 py-4 bg-indigo-900/80 border-t border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
          <div className="flex items-center space-x-2 text-indigo-300 font-medium">
            <ShieldCheck className="w-4 h-4 text-lime-400 shrink-0" />
            <span>Tested with dark-mode email clients (Apple Mail, Gmail, Outlook, iOS Mail)</span>
          </div>

          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={handleCopyHtml}
              className="px-4 py-2 bg-indigo-950 hover:bg-indigo-800 text-indigo-200 border border-white/10 rounded-xl font-bold transition flex items-center space-x-1.5"
            >
              {copiedHtml ? <Check className="w-3.5 h-3.5 text-lime-400" /> : <Copy className="w-3.5 h-3.5" />}
              <span>{copiedHtml ? 'HTML Copied' : 'Copy HTML'}</span>
            </button>

            <button
              type="button"
              disabled={isSendingTest || !simEmail}
              onClick={handleSendTestFromModal}
              className="px-5 py-2 bg-lime-400 hover:bg-lime-300 disabled:opacity-50 text-black font-black uppercase rounded-xl transition shadow-lg shadow-lime-400/20 flex items-center space-x-2"
            >
              {isSendingTest ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-black" />
                  <span>Dispatching...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Dispatch Test to {simEmail}</span>
                </>
              )}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};
